import { Component, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Validators } from '@angular/forms';
import { cValidators } from '../../models/validator';
import { IvFormComponent } from '../../shared/components/iv-form/iv-form.component';
import { CertService, UserService } from '../../api';
import { TranslateService } from '../../core/translation';
import { MessageService } from './../../shared/message/message.service';
import { LoadingService } from './../../shared/loading/loading.service';
import { UserRegisterRequest } from '../../api/model/userRegisterRequest';
import { PersonalizationsService } from '../../base/services/personalizations/personalizations.service';
import { Environment } from '../../models/environment';
import { CertificateService } from '../../finish/services/certificate.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


@Component({
  selector: 'app-alternative',
  templateUrl: './alternative.component.html',
  styleUrls: ['./alternative.component.scss'],
  providers: [CertService, UserService]
})

export class AlternativeComponent implements OnDestroy {
  /**
   * Object for the IvForm Component
   */
  @ViewChild('formUser') formUser: IvFormComponent;

  /**
   * Object for the form configuration
   */
  config;

  /**
   * Object for the user form configuration
   */
  configFormUser = this.parseConfig('user', 2);

  /**
   * Object for the organization form configuration
   */
  configFormOrganization = this.parseConfig('orga', 2);

  /**
   * Object for the error messages of form 1
   */
  erroresForm: any = {
    'required': this._translate.instant('register_error_required'),
    'email': this._translate.instant('form_error_email'),
    'nif': this._translate.instant('form_error_nifcif'),
    'cif': this._translate.instant('form_error_cif'),
  };

  /**
   * Object for the error messages of form 2
   */
  erroresForm2: any = {
    'required': this._translate.instant('form_error_required'),
    'email': this._translate.instant('form_error_email'),
    'nif': this._translate.instant('form_error_nifcif'),
    'cif': this._translate.instant('form_error_cif'),
    'repeat': this._translate.instant('form_error_repeat')
  };

  /**
   * Object for the form buttons configs
   */
  botonesForm: { label: string, active: boolean }[] = [
    { label: this._translate.instant('btn_limpiar'), active: true },
    { label: this._translate.instant('btn_guardar'), active: true }
  ];

  /**
   * Object for the enviroment configuration
   */
  environment: Environment;

  _transParam: boolean = false;

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private router: Router,
    private _certApi: CertService,
    private _userApi: UserService,
    public _translate: TranslateService,
    private _message: MessageService,
    private _loading: LoadingService,
    private _personalization: PersonalizationsService,
    private _cert: CertificateService) {
    this.environment = this._personalization.sendEnv;
    this.config = this.environment?.registerConfig;
    if (!this.environment?.registerConfig) {
      this.router.navigate(['/']);
    }
    TranslateService.initTranslateSubscription(this);
  }

  ngOnDestroy() {
    this.destroy$.unsubscribe();
  }

  parseConfig(formKey: string, type: number) {
    const c = this.config[formKey];
    const obj: any = {}
    for (const key in c) {
      if (type === 1) {
        const val = [];
        for (let i = 0; i < c[key].validators.length; i++) {
          val.push(cValidators[c[key].validators[i]])
        }
        obj[key] = ['', Validators.compose(val)];
      } else {
        obj[key] = {
          type: c[key].type,
          label: this._translate.instant('register_form_label_' + formKey + '_' + key),
          autocomplete: c[key].type !== 'password' ? 'off' : 'new-password',
          validators: c[key].validators,
          readonly: c[key].readonly,
          nombreService: key
        }
      }
    }
    return obj;
  }

  translate(): void {
    this._translate.massTranslate(this.configFormUser, 'label', 'register_form_label_user_');
    this.erroresForm.required = this._translate.instant('form_error_required');
    this.erroresForm.minlength = this._translate.instant('form_error_min_length');
    this.erroresForm.maxlength = this._translate.instant('form_error_max_length');
    this.erroresForm.pattern = this._translate.instant('form_error_pattern');
    this.erroresForm.email = this._translate.instant('form_error_email');
    this.erroresForm.password = this._translate.instant('form_error_password');
    this.erroresForm.repeat = this._translate.instant('form_error_pass');
    this.erroresForm.nif = this._translate.instant('form_error_nif');
    this.erroresForm.cif = this._translate.instant('form_error_cif');
  }

  async fillForm(): Promise<void> {
    this._loading.set(true);
    const callbackId = await this._cert.certGate();
    this._certApi.certGetCertificateClient(callbackId, '1').pipe(takeUntil(this.destroy$)).subscribe(response => {
      const data = response.data.parseCertInfo.certinfo

      if (data?.type == 'PRPJ' || data?.type == 'PR' || data?.type == 'PJ' || data?.type == 'PRAP') {
        if (data.org) {
          this.formUser.Form.patchValue({
            name: data?.org.name,
            nif: data?.org.doc,
            email: data?.org.email
          });
          this.formUser.Form.updateValueAndValidity();
        } else if (data?.user) {
          this.formUser.Form.patchValue({
            name: data?.user.name + ' ' + data?.user.surname,
            nif: data?.user.doc,
            email: data?.user.email
          });
          this.formUser.Form.updateValueAndValidity();
        }
      } else {
        if (data?.user) {
          this.formUser.Form.patchValue({
            name: data?.user.name + ' ' + data?.user.surname,
            nif: data?.user.doc,
            email: data?.user.email
          });
          this.formUser.Form.updateValueAndValidity();
        }
      }

      this._loading.set(false);
    }, error => {
      const err = error.status ? this._message.renderError(error) : this._translate.instant('register_error_certificate');
      this._loading.set(false);
      this._message.showError(err);
    });
  }

  submit(): void {
    this._loading.set(true);
    const user = this.formUser?.Form.value;
    const request: UserRegisterRequest = {
      name: user?.name,
      nif: user?.nif,
      email: user?.email,
      orga: {
        name: user?.name,
        cif: user?.nif,
        email: user?.email
      }
    };
    this._userApi.userPostRegister(request, '1').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this._message.show(this._translate.instant('register_success_message'), 'success', this._translate.instant('dialog_success_title'));
      this._loading.set(false);
      this.router.navigate(['/']);
    }, error => {
      this._message.showError(error)
      this._loading.set(false);
    });
  }

  clear(): void {
    this.formUser.Form.reset();
  }

  toHome(): void {
    this.router.navigate(['']);
  }

}
